import * as Handsontable from "handsontable";
import { C } from "../common/component";
import { Helper } from "../common/helper";
import { TimesheetChange, TimesheetRightTableDataManager } from "./timesheet-right-table-data-manager";
import { TableHelper } from "../common/table-helper";
import { TimesheetCommentsManager } from "./timesheet-comments-manager";
import { TimesheetCellButton, TimesheetRightTableColumn } from "./timesheet-models";
import { Form, FormDisplayer } from "../forms/form";
import { MessageDisplayer } from "../common/message-displayer";
import { Tooltip } from "../common/tooltip";
import { EditableTableCalculator } from "../common/editable-table";
import { DomainHelper } from "../common/domain-helper";
import { Txt } from "../common/text";
import { TimesheetContextMenuBuilder } from "./timesheet-context-menu-builder";
import { TimesheetCopyPasteManager } from "./timesheet-copy-paste-manager";
import { TimesheetRightTableFormatter } from "./timesheet-right-table-formatter";
import { TimesheetShortcutsSet } from "./timesheet-shortcuts-set";

export class TimesheetRightTable
{
    private readonly doNotTriggerSave = "doNotTriggerSave";

    private readonly columns: TimesheetRightTableColumn[];
    private readonly rowHeight: number;
    private readonly rowHeaderWidth: number;
    private readonly width: number;
    private readonly multiCellButtons: TimesheetCellButton[];
    private readonly isCompact: boolean;
    private readonly HideRightTimeSheetShortcuts: boolean;
    private readonly DisableDeletingMoreThanOneItem: boolean;
    private dataManager: TimesheetRightTableDataManager;
    private copyPasteManager?: TimesheetCopyPasteManager;
    private customContextMenu?: TimesheetContextMenuBuilder;

    private headerHeight: number;
    private rightTable: any;
    private rightColumn: any;
    private hot: Handsontable;
    private additionalHeader: any;
    private isScrolling = false;
    private scrollThreshold = 10;

    private boundKeyHandler?: (e: KeyboardEvent) => void;

    constructor(columns: TimesheetRightTableColumn[],
                rowHeight: number,
                rowHeaderWidth: number,
                width: number,
                multiCellButtons: TimesheetCellButton[],
                isCompact: boolean,
                HideRightTimeSheetShortcuts: boolean,
                DisableDeletingMoreThanOneItem: boolean)
    {
        this.columns = columns;
        this.rowHeight = rowHeight;
        this.rowHeaderWidth = rowHeaderWidth;
        this.width = width;
        this.multiCellButtons = multiCellButtons;
        this.isCompact = isCompact;
        this.HideRightTimeSheetShortcuts = HideRightTimeSheetShortcuts;
        this.DisableDeletingMoreThanOneItem = DisableDeletingMoreThanOneItem;
    }

    appendTo(parent,
             dataManager: TimesheetRightTableDataManager,
             formatter: TimesheetRightTableFormatter,
             commentsManager: TimesheetCommentsManager,
             calculator: EditableTableCalculator,
             shortcuts: TimesheetShortcutsSet)
    {
        this.dataManager = dataManager;
        this.copyPasteManager = new TimesheetCopyPasteManager(dataManager);

        this.rightColumn = C.div.addClass(`col-xs-${12 - this.width} schedule-col`)
            .css("height", C.leftTable.height())
            .css("overflow", "hidden")
            .css("flex", Helper.isMobileScreen() ? "1 1 auto" : "")
            .appendTo(parent);
        const rightColumn = this.rightColumn;
        this.rightTable = C.table.addClass("table table-bordered table-sm schedule-table")
            .appendTo(rightColumn);

        const startDate = Helper.stringToDate(this.columns[0].Date);
        const endDate = Helper.stringToDate(this.columns[this.columns.length - 1].Date);
        const headerRow = TableHelper.createMonthsHeaderRowFromDates(startDate, endDate);

        const data: string[][] = [];
        for (const row of dataManager.getData().getAllRows())
        {
            const rowData: string[] = [];
            for (const cell of row.Cells)
            {
                rowData.push(cell.EditValue);
            }
            data.push(rowData);
        }

        this.additionalHeader = null;
        if (this.columns[0].AdditionalCell)
        {
            this.additionalHeader = C.tr.css("height", "24px");
            C.th.appendTo(this.additionalHeader);
            for (const column of this.columns)
            {
                calculator.addCell(C.div.appendTo(C.th.appendTo(this.additionalHeader)), column.AdditionalCell);
            }
        }

        const getHotTableHeaders = () => document.querySelectorAll<HTMLElement>(".htCore thead");
        const handsontableConfig: any = {
            autoWrapRow: true,
            beforeRender: () => getHotTableHeaders().forEach(header => header.replaceChildren()),
            afterRender: () =>
            {
                const headers = getHotTableHeaders();
                headerRow.prependTo(Array.from(headers));
                for (let i = 0; i < this.columns.length; i++)
                {
                    if (this.columns[i].Tooltip)
                    {
                        headers.forEach(header =>
                        {
                            const anchor = header.children[1]?.children[i + 1]?.children[0];
                            if (anchor)
                            {
                                Tooltip.attach(anchor as HTMLElement, this.columns[i].Tooltip);
                            }
                        });
                    }
                }
                if (this.additionalHeader)
                {
                    this.additionalHeader.appendTo(headers);
                    if (DomainHelper.getDomain() === "yolo")
                    {
                        headers.forEach(header =>
                        {
                            for (let i = 0; i < this.columns.length; i++)
                            {
                                const anchor = header.children[2]?.children[i + 1]?.children[0];
                                if (!anchor)
                                {
                                    continue;
                                }
                                Tooltip.attach(anchor as HTMLElement, () =>
                                {
                                    const shifts = {};
                                    for (const row of dataManager.getData().getAllRows())
                                    {
                                        const cell = row.Cells[i];
                                        const shortcut = shortcuts.tryGetByAbbreviation(cell.EditValue);
                                        if (shortcut && shortcut.Type !== "VacationType")
                                        {
                                            if (!shifts[cell.EditValue])
                                            {
                                                shifts[cell.EditValue] = { count: 0, shortcut: shortcut };
                                            }
                                            shifts[cell.EditValue].count += 1;
                                        }
                                    }
                                    const array = Object.keys(shifts).map(key => [key, shifts[key]]);
                                    array.sort((first, second) =>
                                        first[1].shortcut.Start + first[1].shortcut.End < second[1].shortcut.Start + second[1].shortcut.End ? -1 : 1);
                                    return array.map(x => x[0] + " " + x[1].count).join(", ");
                                });
                            }
                        });
                    }
                }
            },
            currentHeaderClassName: "",
            viewportColumnRenderingOffset: 1000,
            rowHeights: this.rowHeight,
            maxRows: data.length,
            maxCols: this.columns.length,
            colHeaders: (columnNumber) => formatter.getColumnHeader(columnNumber),
            rowHeaders: (rowNumber) => formatter.getRowHeader(rowNumber, commentsManager),
            rowHeaderWidth: (this.HideRightTimeSheetShortcuts && Helper.isMobileScreen() === false) ? 0 : this.rowHeaderWidth,
            data: data,
            cells: (rowNumber, columnNumber) => ({ readOnly: !dataManager.isCellEditable(rowNumber, columnNumber) }),
            renderer: (instance, td, row, col, prop, value, cellProperties) =>
            {
                Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, value, cellProperties]);
                const cell = C.create(td);
                formatter.formatCell(row, col, cell, value);
                commentsManager.configureComment(cell, row, col);
                return td;
            },
            afterChange: (changes, source) =>
            {
                if (source !== "loadData" && source !== this.doNotTriggerSave)
                {
                    const convertedChanges: TimesheetChange[] = [];
                    for (const change of changes)
                    {
                        // Workaround for handsontable bug https://github.com/handsontable/handsontable/issues/5477
                        if (change[3] && change[3].endsWith("\r"))
                        {
                            change[3] = change[3].substring(0, change[3].length - 1);
                        }
                        convertedChanges.push(new TimesheetChange(change[0], change[1], change[2], change[3]));
                    }
                    dataManager.save(convertedChanges, change =>
                        this.hot.setDataAtCell(change.rowNumber, change.columnNumber, change.oldValue, this.doNotTriggerSave));
                }
            },
            beforeAutofill: (start, end) =>
            {
                this.copyPasteManager.copyFromAutofill(start, end);
            },
            beforeChange: (changes, source) =>
            {
                if (source === "Autofill.fill")
                {
                    return false;
                }

                if (!changes || !this.hot)
                {
                    return;
                }

                const selected = this.hot.getSelected();
                if (!selected || selected.length === 0)
                {
                    return;
                }

                const isMultiSelection = selected.some(sel => sel[0] !== sel[2] || sel[1] !== sel[3]);

                const isDeleteAction = changes.every(c => c[3] === "" || c[3] === null);

                if (isMultiSelection && isDeleteAction && this.DisableDeletingMoreThanOneItem)
                {
                    MessageDisplayer.showError(Txt.timesheetMassDeleteNotAllowed);
                    return false;
                }
            },
            contextMenu: { items: {} }
        };

        if (this.isCompact)
        {
            handsontableConfig.colWidths = 40;
        }

        this.hot = new Handsontable(this.rightTable[0], handsontableConfig);
        this.copyPasteManager.attach(this.rightTable[0], this.hot);

        // disable built-in context menu and attach custom one
        const contextMenuPlugin = this.hot.getPlugin("contextMenu");
        if (contextMenuPlugin && typeof contextMenuPlugin.disablePlugin === "function")
        {
            contextMenuPlugin.disablePlugin();
            this.hot.addHook("afterRender", () =>
            {
                this.hot.getPlugin("contextMenu")?.disablePlugin();
            });
        }

        this.customContextMenu =
            new TimesheetContextMenuBuilder(commentsManager, dataManager, formatter, this.copyPasteManager, this.multiCellButtons);
        this.customContextMenu.attach(this.hot);

        this.attachKeyListener();
        this.setupMobileScrollBehavior();

        this.setFixedHeader();

        const dropTarget = this.rightTable[0] as HTMLElement;
        dropTarget.addEventListener("dragover", (e: DragEvent) =>
        {
            e.preventDefault();
            if (e.dataTransfer)
            {
                e.dataTransfer.dropEffect = "copy";
            }
        });
        dropTarget.addEventListener("drop", (e: DragEvent) =>
        {
            e.preventDefault();
            const td = (e.target as HTMLElement)?.closest("td");
            if (!td)
            {
                return;
            }
            const tr = td.parentElement as HTMLElement;
            const tbody = tr.parentElement as HTMLElement;
            const col = Array.prototype.indexOf.call(tr.children, td) - 1;
            const row = Array.prototype.indexOf.call(tbody.children, tr);
            const value = e.dataTransfer ? e.dataTransfer.getData("text/plain") : "";
            if (col >= 0 && row >= 0 && dataManager.isCellEditable(row, col))
            {
                this.hot.setDataAtCell(row, col, value);
            }
        });
    }

    setHeaderHeight(height: number)
    {
        if (this.additionalHeader)
        {
            height -= 24;
        }
        if (this.headerHeight !== height)
        {
            (<any>this.hot).updateSettings({ columnHeaderHeight: height });
            this.headerHeight = height;
        }
    }

    setDataAtCellsWithoutSave(changes: any[][])
    {
        this.hot.setDataAtCell(<any>changes, this.doNotTriggerSave);
    }

    render()
    {
        this.hot.render();
    }

    setFixedHeader()
    {
        const header = C.hotClone;
        const rightTable = C.rightTable;
        const windowQuery = C.window;

        windowQuery.on("scroll", () =>
        {
            if (windowQuery.scrollTop() >= rightTable.offset().top)
            {
                header.css({
                    position: "fixed",
                    top: "0",
                    left: this.rightColumn.offset().left + 2,
                    right: ""
                });
            }
            else
            {
                header.css("position", "relative");
            }
        });
    }

    private setupMobileScrollBehavior()
    {
        if (!Helper.isMobileScreen())
        {
            return;
        }

        const tableElement = this.rightTable[0];
        let startX = 0, startY = 0;

        const onTouchStart = (e: TouchEvent) =>
        {
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            this.isScrolling = false;
        };

        const onTouchMove = (e: TouchEvent) =>
        {
            const touch = e.touches[0];
            if (!touch)
            {
                return;
            }

            const dx = touch.clientX - startX;
            const dy = touch.clientY - startY;
            const movedEnough = dx * dx + dy * dy > this.scrollThreshold ** 2;

            if (movedEnough)
            {
                this.isScrolling = true;
                this.deselectActiveCell();
            }
        };

        const onTouchEnd = () =>
        {
            // Reset after small delay to avoid accidental re-taps
            setTimeout(() => (this.isScrolling = false), 100);
        };

        tableElement.addEventListener("touchstart", onTouchStart, { passive: true });
        tableElement.addEventListener("touchmove", onTouchMove, { passive: true });
        tableElement.addEventListener("touchend", onTouchEnd, { passive: true });
    }
    private deselectActiveCell()
    {
        if (!this.hot)
        {
            return;
        }

        this.hot.deselectCell();
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement.blur)
        {
            activeElement.blur();
        }

    }

    private attachKeyListener(): void
    {
        const container = this.rightTable?.[0];
        if (!container)
        {
            return;
        }

        if (this.boundKeyHandler)
        {
            container.removeEventListener("keydown", this.boundKeyHandler);
        }

        this.boundKeyHandler = (e: KeyboardEvent) =>
        {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "e")
            {
                e.preventDefault();
                this.handleEditShortcut();
            }
        };

        container.addEventListener("keydown", this.boundKeyHandler);
    }

    destroy(): void
    {
        if (this.boundKeyHandler && this.rightTable?.[0])
        {
            this.rightTable[0].removeEventListener("keydown", this.boundKeyHandler);
        }
        this.customContextMenu?.destroy();
        this.copyPasteManager?.destroy();
        this.hot?.destroy();
    }

    private handleEditShortcut(): void
    {
        if (!this.hot || !this.dataManager)
        {
            return;
        }

        const selected = this.hot.getSelectedLast();
        if (!selected)
        {
            return;
        }

        const [row, col] = selected;
        const cellData = this.dataManager.getData().getCell(row, col);
        if (!cellData || !cellData.EditFormUrl)
        {
            MessageDisplayer.showWarning(Txt.noEditFormAvailable);
            return;
        }

        const form = new Form(cellData.EditFormUrl);
        form.onDataChanged = () =>
        {
            this.dataManager.reloadRowData(row, row);
        };
        FormDisplayer.openInModal(form);
        this.deselectActiveCell();
    }
}
